import { T, always, cond, equals, find, propEq } from 'ramda';
import React, { FC } from 'react';
import styled from 'styled-components';
import 'swiper/css';
import { Swiper, SwiperSlide } from 'swiper/react';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';

import TopicCard from '@ui/molecules/TopicCard';

import { ITopic } from '@ui/routes/Dashboard/GetCare';

import usePrefersReducedMotion from '@hooks/usePrefersReducedMotion';
import useWindowSize from '@hooks/useWindowSize';

import { interpolate } from '@utils/butter';
import { exists } from '@utils/general';

import theme from '@theme/FolxTheme';

interface ITopicCardSliderProps {
	category: string;
	inactiveTooltip: string;
	topics: ITopic[];
	onClick?: (args: {
		topic: string;
		flow: string;
		id: number;
		mhId?: string;
	}) => React.MouseEventHandler<HTMLElement>;
	state?: string;
	stateTooltips?: Array<{ stateId: string; text: string }>;
	stateTopicTooltips?: Array<{ stateId: string; topicId: string; text: string }>;
	tooltips?: Array<{ topicId: string; text: string }>;
}

const TopicsRow = styled(FlexRow)`
	flex-wrap: wrap;
	margin-left: -${theme.spacing.xssm};
`;

const TopicCardSlider: FC<ITopicCardSliderProps> = ({
	category,
	inactiveTooltip,
	onClick = (topic) => (e) => {
		return;
	},
	state = '',
	stateTooltips = [],
	stateTopicTooltips = [],
	tooltips = [],
	topics,
}) => {
	const { isMobile } = useWindowSize(theme.responsive.sizes.laptop);
	const shouldAnimate = !usePrefersReducedMotion();

	const getTooltipDescription = (service: string, state?: string) =>
		interpolate(inactiveTooltip, { service, state: state ? state : 'your state' });

	if (topics.every(({ isActive }) => !isActive)) {
		return (
			<FlexColumn gap="sm">
				<BasicText variant="pill">{category}</BasicText>
				<BasicText variant="fineprint">{getTooltipDescription(category, state)}</BasicText>
			</FlexColumn>
		);
	}

	const findTooltip = (topicId: number, stateId: string) => {
		const stateTopic = find((tooltip) => {
			return `${topicId}` === tooltip.topicId && stateId === tooltip.stateId;
		}, stateTopicTooltips);
		return exists(stateTopic?.text)
			? stateTopic?.text
			: find(propEq('topicId', topicId.toString()), tooltips)?.text;
	};
	const findStateTooltip = (stateId: string) => {
		return find(propEq('stateId', stateId), stateTooltips)?.text;
	};

	const getTooltip = (topicCard: ITopic) => {
		const tooltip = cond<ITopic, boolean, string>([
			[(t: ITopic) => equals(true, t.ineligible), always(findStateTooltip(state) ?? '')],
			[
				(t: ITopic) => equals(false, t.isActive),
				always(getTooltipDescription(topicCard.topic, state)),
			],
			[T, always(findTooltip(topicCard.id, state) ?? '')],
		])(topicCard);

		return tooltip;
	};

	return (
		<FlexColumn gap="sm">
			<BasicText variant="pill">{category}</BasicText>
			{isMobile && shouldAnimate ? (
				<Swiper
					slidesPerView={'auto'}
					spaceBetween={12}
					slidesOffsetAfter={32}
					slidesOffsetBefore={32}
					style={{
						marginLeft: '-4.4rem',
						marginRight: '-2rem',
						overflow: 'visible',
						paddingBottom: '2rem',
					}}>
					{topics.map((topicCard) => (
						<SwiperSlide style={{ width: 'auto' }} key={topicCard.id}>
							<TopicCard
								topic={topicCard.topic}
								flow={topicCard.path}
								id={topicCard.id}
								mhId={topicCard?.mhId}
								onClick={onClick}
								disabled={!topicCard.isActive}
								showTooltip={exists(getTooltip(topicCard))}
								tooltipDescription={getTooltip(topicCard)}
							/>
						</SwiperSlide>
					))}
				</Swiper>
			) : (
				<TopicsRow gap="xssm">
					{topics.map((topicCard) => (
						<TopicCard
							key={topicCard.id}
							topic={topicCard.topic}
							flow={topicCard.path}
							id={topicCard.id}
							mhId={topicCard?.mhId}
							onClick={onClick}
							disabled={!topicCard.isActive}
							showTooltip={exists(getTooltip(topicCard))}
							tooltipDescription={getTooltip(topicCard)}
						/>
					))}
				</TopicsRow>
			)}
		</FlexColumn>
	);
};

export default styled(TopicCardSlider)``;
